import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { motion } from "framer-motion";
import {
  Car,
  Plus,
  Trash2,
  Loader2,
} from "lucide-react";

export default function AdminCars() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    plate: "",
    image: "",
  });

  /* ================= LOAD CARS ================= */
  const loadCars = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "cars"));
    setCars(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => {
    loadCars();
  }, []);

  /* ================= HELPERS ================= */
  const update = (k, v) =>
    setForm((f) => ({ ...f, [k]: v }));

  /* ================= SUBMIT ================= */
  const submit = async () => {
    if (!form.name || !form.plate || !form.image) {
      alert("Car Name, Plate Number and Image URL are required");
      return;
    }

    try {
      setSaving(true);
      await addDoc(collection(db, "cars"), {
        name: form.name,
        plate: form.plate,
        image: form.image,
        createdAt: serverTimestamp(),
      });

      setForm({ name: "", plate: "", image: "" });
      loadCars();
    } finally {
      setSaving(false);
    }
  };

  const removeCar = async (id) => {
    if (!window.confirm("Delete this car?")) return;
    await deleteDoc(doc(db, "cars", id));
    loadCars();
  };

  /* ================= UI ================= */
  return (
    <div className="min-h-screen px-6 py-16 bg-[#0b0b0b] text-white">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto"
      >
        <h2 className="text-3xl font-bold mb-8 flex items-center gap-2">
          <Car /> Manage Hearse Cars
        </h2>

        {/* ADD FORM */}
        <div className="rounded-3xl p-6 mb-10 bg-[#111214] border border-white/10">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Plus size={18} /> Add New Hearse
          </h3>

          <div className="grid md:grid-cols-3 gap-4">
            <input
              placeholder="Car Name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="px-4 py-3 rounded-xl border bg-[#151820] border-white/20 text-white"
            />
            <input
              placeholder="Plate Number"
              value={form.plate}
              onChange={(e) => update("plate", e.target.value)}
              className="px-4 py-3 rounded-xl border bg-[#151820] border-white/20 text-white"
            />
            <input
              placeholder="Image URL"
              value={form.image}
              onChange={(e) => update("image", e.target.value)}
              className="px-4 py-3 rounded-xl border bg-[#151820] border-white/20 text-white"
            />
          </div>

          <button
            onClick={submit}
            disabled={saving}
            className="mt-6 px-6 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 font-semibold flex items-center gap-2"
          >
            {saving && <Loader2 className="animate-spin" />}
            Save Car
          </button>
        </div>

        {/* CAR LIST */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {cars.map((c) => (
              <div
                key={c.id}
                className="rounded-2xl overflow-hidden bg-[#111214] border border-white/10"
              >
                <img
                  src={c.image}
                  alt={c.name}
                  className="h-40 w-full object-cover"
                />
                <div className="p-4">
                  <p className="font-semibold">{c.name}</p>
                  <p className="text-sm opacity-60">{c.plate}</p>

                  <button
                    onClick={() => removeCar(c.id)}
                    className="mt-3 text-red-400 flex items-center gap-1 text-sm"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
